import React from 'react';
import { Link } from 'react-router-dom';
import { IMAGES } from '../constants/images';
import styles from './AboutPage.module.css';
import SEO, { BUSINESS_SCHEMA } from '../components/SEO';

const ABOUT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    { ...BUSINESS_SCHEMA, '@id': 'https://movementgreen.com.au/#business' },
    {
      '@type': 'AboutPage',
      '@id':   'https://movementgreen.com.au/about#webpage',
      url:     'https://movementgreen.com.au/about',
      name:    'About Movement Green — VEU Accredited Provider Victoria',
      description: 'Learn about Movement Green, a VEU Accredited Person helping homeowners access VEU government rebates on heat pumps, reverse-cycle AC, LED lighting and more.',
      about: { '@id': 'https://movementgreen.com.au/#business' },
    },
  ],
};

const VALUES = [
  { n:'01', title:'Scheme Compliance', desc:'Every upgrade we install meets Victorian Energy Upgrades (VEU) program* requirements — ensuring your rebate is properly applied and legitimate.' },
  { n:'02', title:'Victorian Focus', desc:'We exclusively serve Victorian homes and businesses, giving us deep expertise in local rebate eligibility and VEU-approved product requirements.' },
  { n:'03', title:'Maximum Rebate Value', desc:'We identify every eligible upgrade for your property to ensure you access the full government rebate entitlements available under the VEU program.*' },
];

const STEPS = [
  { n:'01', title:'Free Energy Assessment', desc:'We visit your Victorian home, assess your current appliances and energy use, and identify all upgrades that qualify for government rebates under the VEU program* — at no cost.' },
  { n:'02', title:'Tailored Upgrade Plan', desc:'Our team recommends a personalised set of VEU-eligible upgrades — heat pumps, LED lighting, reverse-cycle AC — matched to your home, budget, and rebate entitlements.' },
  { n:'03', title:'Professional Installation', desc:'As a VEU Accredited Provider, we complete your upgrade to scheme standards quickly and cleanly and handle all the scheme paperwork ourselves — you receive your VEU benefit with zero hassle.' },
];

export default function AboutPage() {
  return (
    <main className="page-enter">
      <SEO
        title="About Movement Green | VEU Accredited Provider Victoria"
        description="Movement Green is a VEU Accredited Provider delivering Victorian Energy Upgrades across Victoria — helping homeowners access government rebates on heat pumps, reverse-cycle AC, LED lighting and more. Free assessment."
        keywords="Movement Green Victoria, VEU accredited provider Melbourne, VEU accredited person Victoria, Victorian Energy Upgrades company, energy upgrade assessment Victoria"
        schema={ABOUT_SCHEMA}
      />

      {/* ── HERO — eco home at dusk ──────────────── */}
      <section className={styles.heroSection}>
        <div className={styles.heroWrap}>
          <div className={styles.heroText}>
            <span className="eyebrow">Who We Are</span>
            <h1 className={`disp-xl ${styles.heroH1}`}>
              Victoria's Energy<br/><em>Upgrade Team.</em>
            </h1>
            <p className="body-lg">
              Movement Green helps Victorian homeowners access government rebates<sup>*</sup>
              and upgrade to energy-efficient appliances — cutting bills and
              emissions at the same time.
            </p>
          </div>
          <div className={styles.heroImgWrap}>
            <img src={IMAGES.aboutHero} alt="Modern eco-home at dusk" className={styles.heroImg}/>
            <div className={styles.heroImgOverlay}/>
            <div className={styles.heroFloatCard}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 3C21 3 12 3 7 8C2 13 3 21 3 21C3 21 11 22 16 17C21 12 21 3 21 3Z" fill="#B5D96A"/>
              </svg>
              <div>
                <div className={styles.floatTitle}>Victorian Energy Upgrades</div>
                <div className={styles.floatBody}>Helping homeowners access government rebates.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION + PHOTO GRID ─────────────────── */}
      <section className={styles.missionSection}>
        <div className={styles.missionWrap}>
          <div className={styles.missionLeft}>
            <span className="eyebrow">Our Mission</span>
            <h2 className={`disp-md ${styles.missionTitle}`}>
              Making Energy<br/>Upgrades<br/><em>Simple.</em>
            </h2>
            <p className="body-md">
              The Victorian Energy Upgrades program<sup>*</sup> offers significant government rebates on
              energy-efficient appliances — but navigating eligibility, products, and paperwork
              can be daunting. We exist to make that easy. We assess your home, recommend the
              right upgrades, install them properly, and handle everything in between.
            </p>
            <div className={styles.values}>
              {VALUES.map(v => (
                <div key={v.n} className={styles.value}>
                  <span className={styles.valueNum}>{v.n}</span>
                  <div>
                    <div className={styles.valueTitle}>{v.title}</div>
                    <div className={styles.valueDesc}>{v.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Solar panels + biophilic interior */}
          <div className={styles.photoGrid}>
            <div className={styles.photoTop}>
              <img src={IMAGES.aboutPhoto1} alt="Solar panels against blue sky" className={styles.photo}/>
              <div className={styles.photoLabel}>Solar PV Installation</div>
            </div>
            <div className={styles.photoBottom}>
              <img src={IMAGES.aboutPhoto2} alt="Biophilic interior with plants" className={styles.photo}/>
              <div className={styles.photoLabel}>LED Lighting Upgrade</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS ─────────────────────────────── */}
      <section className={styles.processSection}>
        <div className={styles.processWrap}>
          <div className={styles.processHead}>
            <span className="eyebrow">How It Works</span>
            <h2 className={`disp-md ${styles.processTitle}`}>
              From Assessment<br/><em>to Savings.</em>
            </h2>
          </div>
          <div className={styles.steps}>
            {STEPS.map(s => (
              <div key={s.n} className={styles.step}>
                <div className={styles.stepNum}>{s.n}</div>
                <div className={styles.stepLine}/>
                <h3 className={`h-lg ${styles.stepTitle}`}>{s.title}</h3>
                <p className="body-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────── */}
      <section className={styles.cta}>
        <div className={styles.ctaWrap}>
          <h2 className={`disp-md ${styles.ctaTitle}`}>See what rebates<br/>your home <em>qualifies for.</em></h2>
          <div className={styles.ctaBtns}>
            <Link to="/contact" className="btn btn-primary btn-lg">Book Free Assessment →</Link>
            <Link to="/solutions" className="btn btn-outline btn-lg">View Upgrades</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
